import * as Workflow from './models/workflow';

import { Common } from './common';
import {
  Note,
  NoteMetric,
  FindAllOptions,
  FieldUpdate,
  Reason,
  CarryKind,
  CarryKinds,
  UpdateNoteWorkflowResponse,
  Kind,
} from './models/note';
import { NoteField, NoteFieldWithConfig } from './models/note/field';

export class API {
  common: Common;

  constructor(common: Common) {
    this.common = common;
  }

  async findAll(options: FindAllOptions): Promise<Note[]> {
    const url = this.common.constructPath('/note', options);
    const { notes } = await this.common.getJSON(url);
    return notes;
  }

  async findByEncounterId(
    encounterId: string,
    includeDeleted = false,
  ): Promise<Note[]> {
    const url = this.common.constructPath(`/note`, {
      encounterId,
      includeDeleted,
    });
    const { notes } = await this.common.getJSON(url);
    return notes;
  }

  async findOneNotableByEncounterId(
    encounterId: string,
    includeDeleted = false,
  ): Promise<Note | undefined> {
    const notes = await this.findByEncounterId(encounterId, includeDeleted);
    return notes.find((note) => note.kind === Kind.Notable);
  }

  async findOneById(id: string): Promise<Note> {
    const url = this.common.constructPath(`/note/${id}`);
    const { note } = await this.common.getJSON(url);
    return note;
  }

  async updateReviewFields(
    id: string,
    values: {
      contentNeedsReviewReasons?: Reason[];
      contentNeedsReviewComment?: string;
    },
  ): Promise<Note> {
    const url = this.common.constructPath(`/note/${id}`);
    return this.common.putJSON(url, { ...values });
  }

  async findFieldsByNoteId(noteId: string): Promise<NoteFieldWithConfig[]> {
    const url = this.common.constructPath(`/note/${noteId}/fields`);
    const { fields } = await this.common.getJSON(url);
    return fields;
  }

  async updateField(id: string, value: string): Promise<NoteFieldWithConfig> {
    const url = this.common.constructPath(`/note/field/${id}`);
    return this.common.putJSON(url, { value });
  }

  async applyCarryData(
    kind: CarryKind,
    noteId: string,
    section?: string,
  ): Promise<{ fields: NoteField[] }> {
    let pathParams = `/note/${noteId}/applyCarry`;
    switch (kind) {
      case CarryKinds.Current:
        pathParams += `Current`;
        break;
      case CarryKinds.Forward:
        pathParams += `Forward`;
        break;
      default:
        throw new Error('Invalid carry kind!');
    }
    const url = this.common.constructPath(pathParams);
    return this.common.postJSON(url, { section });
  }

  async getFormattedNote(noteId: string): Promise<{ formattedNote: string }> {
    const url = this.common.constructPath(`/note/${noteId}/formatted`);
    return this.common.getJSON(url);
  }

  async getNoteDiff(
    noteId: string,
  ): Promise<{ actual: string; expected: string } | null> {
    const url = this.common.constructPath(`/note/${noteId}/diff`);
    return this.common.getJSON(url);
  }

  async getNoteTranscript(noteId: string): Promise<{ transcript: string }> {
    const url = this.common.constructPath(`/note/${noteId}/transcript`);
    return this.common.getJSON(url);
  }

  async getNoteAsrTranscript(noteId: string): Promise<{ transcript: string }> {
    const url = this.common.constructPath(`/note/${noteId}/transcript/asr`);
    return this.common.getJSON(url);
  }

  async getNoteTranscriptDiff(
    noteId: string,
  ): Promise<{ expected: string; actual: string }> {
    const [expected, actual] = await Promise.all([
      this.getNoteTranscript(noteId),
      this.getNoteAsrTranscript(noteId),
    ]);
    return { expected: expected.transcript, actual: actual.transcript };
  }

  async createNoteTranscript(noteId: string): Promise<{ transcript: string }> {
    const url = this.common.constructPath(`/note/${noteId}/transcript`);
    return this.common.postJSON(url, {});
  }

  async updateNoteTranscript(
    noteId: string,
    transcript: string,
  ): Promise<{ transcript: string }> {
    const url = this.common.constructPath(`/note/${noteId}/transcript`);
    return this.common.patchJSON(url, { transcript });
  }

  async getNoteMetric(noteId: string): Promise<NoteMetric | null> {
    const url = this.common.constructPath(`/note/${noteId}/metric`);
    return this.common.getJSON(url);
  }

  async getNoteWorkflows(
    noteId: string,
    status?: Workflow.Note.Status,
  ): Promise<Workflow.NoteWorkflow[]> {
    const url = this.common.constructPath(`/note/${noteId}/workflows`, {
      status,
    });
    const { workflows } = await this.common.getJSON(url);
    return workflows;
  }

  async exportToEhr(noteId: string): Promise<UpdateNoteWorkflowResponse> {
    const url = this.common.constructPath(`/note/${noteId}/export-to-ehr`);
    return this.common.postJSON(url, {});
  }

  async exportedToEhr(noteId: string): Promise<UpdateNoteWorkflowResponse> {
    const url = this.common.constructPath(`/note/${noteId}/exported-to-ehr`);
    return this.common.postJSON(url, {});
  }

  async exportedToEhrManually(
    noteId: string,
  ): Promise<UpdateNoteWorkflowResponse> {
    const url = this.common.constructPath(
      `/note/${noteId}/exported-to-ehr-manually`,
    );
    return this.common.postJSON(url, {});
  }

  async noAudio(noteId: string): Promise<UpdateNoteWorkflowResponse> {
    const url = this.common.constructPath(`/note/${noteId}/no-audio`);
    return this.common.postJSON(url, {});
  }

  async initCompleted(noteId: string): Promise<UpdateNoteWorkflowResponse> {
    const url = this.common.constructPath(`/note/${noteId}/init/completed`);
    return this.common.postJSON(url, {});
  }

  async fetchingCompleted(noteId: string): Promise<UpdateNoteWorkflowResponse> {
    const url = this.common.constructPath(`/note/${noteId}/fetching/completed`);
    return this.common.postJSON(url, {});
  }

  async transcribingStarted(
    noteId: string,
  ): Promise<UpdateNoteWorkflowResponse> {
    const url = this.common.constructPath(
      `/note/${noteId}/transcribing/started`,
    );
    return this.common.postJSON(url, {});
  }

  async transcribingCompleted(
    noteId: string,
  ): Promise<UpdateNoteWorkflowResponse> {
    const url = this.common.constructPath(
      `/note/${noteId}/transcribing/completed`,
    );
    return this.common.postJSON(url, {});
  }

  async labelingStarted(noteId: string): Promise<UpdateNoteWorkflowResponse> {
    const url = this.common.constructPath(`/note/${noteId}/labeling/started`);
    return this.common.postJSON(url, {});
  }

  async labelingCompleted(
    noteId: string,
    contentNeedsReviewComment?: string,
  ): Promise<UpdateNoteWorkflowResponse> {
    const url = this.common.constructPath(`/note/${noteId}/labeling/completed`);
    return this.common.postJSON(url, { contentNeedsReviewComment });
  }

  async structuringStarted(
    noteId: string,
  ): Promise<UpdateNoteWorkflowResponse> {
    const url = this.common.constructPath(
      `/note/${noteId}/structuring/started`,
    );
    return this.common.postJSON(url, {});
  }

  async structuringCompleted(args: {
    noteId: string;
    contentNeedsReviewComment?: string;
    fieldUpdates?: FieldUpdate[];
    forceReview?: boolean;
  }): Promise<UpdateNoteWorkflowResponse> {
    const { noteId, contentNeedsReviewComment, fieldUpdates, forceReview } =
      args;
    const url = this.common.constructPath(
      `/note/${noteId}/structuring/completed`,
    );
    return this.common.postJSON(url, {
      contentNeedsReviewComment,
      fieldUpdates,
      forceReview,
    });
  }

  async resolvingStarted(noteId: string): Promise<UpdateNoteWorkflowResponse> {
    const url = this.common.constructPath(`/note/${noteId}/resolving/started`);
    return this.common.postJSON(url, {});
  }

  async resolvingCompleted(args: {
    noteId: string;
    contentNeedsReviewComment?: string;
    forceReview?: boolean;
  }): Promise<UpdateNoteWorkflowResponse> {
    const { noteId, contentNeedsReviewComment, forceReview } = args;
    const url = this.common.constructPath(
      `/note/${noteId}/resolving/completed`,
    );
    return this.common.postJSON(url, {
      contentNeedsReviewComment,
      forceReview,
    });
  }

  async canDisplayWorkflow(
    workflowId: string,
  ): Promise<{ cannotDisplayReason: string | null }> {
    const url = this.common.constructPath(
      `/note/workflows/${workflowId}/can-display`,
    );
    return this.common.getJSON(url);
  }
}
